import {
    CreateAddressBody,
    JogUser,
    JogUserAddress,
    JoggCart,
    Membership,
    UserAddressForm,
    UserInfoForm,
} from "@jog/types"

import { AWSClient, JogError } from "./index"

const STORE_ID = process.env.NEXT_PUBLIC_STORE_ID!

export const usersClient = () => AWSClient(process.env.NEXT_PUBLIC_AWS_USERS_ENDPOINT || "")

export const getUserAddresses = () => usersClient()<JogUserAddress[]>({ method: "GET", uri: "/addresses" })

export const assignCart = (anonymousCartID: string) =>
    usersClient()<JoggCart>({
        method: "PUT",
        uri: `/cart`,
        body: { anonymousCartID, store: process.env.NEXT_PUBLIC_STORE_ID },
    })

export const addUserAddress = (userAddress: CreateAddressBody) =>
    usersClient()<JogUserAddress>({ method: "POST", uri: "/address", body: userAddress })

export const changePassword = (previousPassword: string, proposedPassword: string) =>
    usersClient()<void>({ method: "PUT", uri: "/password", body: { previousPassword, proposedPassword } })

export const changeEmail = (newEmail: string, password: string) =>
    usersClient()<JogUser>({ method: "PUT", uri: "/email", body: { newEmail, password } })

export const checkUser = (email: string, token: string) =>
    usersClient()<{ available: boolean; message?: string }>({
        method: "POST",
        uri: "/available",
        body: { token, email },
    })
        .then((res) => res.body)
        .catch(async (e: JogError) => {
            const body = await e.response.json()
            return { available: false, message: body?.error?.message }
        })

export const confirmForgotPassword = (email: string, confirmationCode: string, password: string) =>
    usersClient()<void>({
        method: "POST",
        uri: "/password/confirm",
        body: { email: email.toLowerCase(), confirmationCode, password },
    })

export const createUser = (userInfo: UserInfoForm) => {
    const body = {
        user: {
            email: userInfo.email,
            country: userInfo.country,
            name: userInfo.name?.trim(),
            insertion: userInfo.insertion?.trim(),
            preferredLanguage: process.env.NEXT_PUBLIC_LOCALE,
            familyName: userInfo.familyName?.trim(),
            phoneNumber: userInfo.phoneNumber,
            gender: userInfo.gender,
            loyaltycardnumber: "",
            registrationWebshop: process.env.NEXT_PUBLIC_WEBSHOP!,
            defaultAddress: {
                country: userInfo.country,
                gender: userInfo.gender,
                name: userInfo.name,
                insertion: userInfo.insertion,
                familyName: userInfo.familyName,
                streetName: userInfo.streetName,
                houseNumber: userInfo.houseNumber,
                addition: userInfo.addition,
                postalCode: userInfo.postalCode,
                city: userInfo.city,
                phoneNumber: userInfo.phoneNumber,
            },
        },
        password: userInfo.password,
    }
    return usersClient()<JogUser>({ method: "POST", uri: "/", body })
}

export const getUserData = () => usersClient()<JogUser>({ method: "GET", uri: "/" }).then((res) => res.body)

export const getMyCart = () => usersClient()<JoggCart>({ method: "GET", uri: `/cart?key=${STORE_ID}` })

export const updateUser = ({
    gender,
    name,
    insertion,
    familyName,
    birthDate,
    phoneNumber,
}: {
    gender: string
    name: string
    insertion?: string | undefined
    familyName: string
    birthDate?: string
    phoneNumber?: string | undefined
}) =>
    usersClient()<JogUser>({
        method: "PUT",
        uri: "/",
        body: {
            name: name?.trim(),
            insertion: (insertion || "")?.trim(),
            familyName: familyName?.trim(),
            gender,
            birthDate,
            phoneNumber,
        },
    }).then((res) => res.body)

export const updateUserAddress = (userAddress: UserAddressForm, addressId: string) =>
    usersClient()<void>({
        method: "PUT",
        uri: `/address/${addressId}`,
        body: userAddress,
    })

export const deleteUserAddress = (addressId: string) =>
    usersClient()<void>({ method: "DELETE", uri: `/address/${addressId}` })

export const forgotPassword = (email: string) =>
    usersClient()<void>({
        method: "POST",
        uri: "/password/forgot",
        body: { email: email.toLowerCase() },
    })

// export const getPurchase = () => usersClient()<{ Items: PurchaseItem[] }>({ method: "GET", uri: "/transactions" })

export const getMembership = () =>
    usersClient()<{ membership: Membership | null }>({ method: "GET", uri: "/membership" })
